import { makeAutoObservable, runInAction } from 'mobx'
import organizationApi from '../tenancy/organizationApi'
import {
	acceptTenantContext,
	beginTenantContextTransition,
	setTenantAccessInvalidatedHandler,
} from '../tenancy/tenantContext'
import { clearTenantScopedState } from '../tenancy/tenantScopedState'

export const SELECTED_ORGANIZATION_STORAGE_KEY = 'reldo:selectedOrganizationId'

function browserStorage() {
	return typeof window === 'undefined' ? null : window.localStorage
}

function errorCode(error) {
	return error?.response?.data?.error?.code || error?.code || 'ORGANIZATION_BOOTSTRAP_FAILED'
}

function isAccessInvalid(error) {
	return error?.response?.status === 403
		|| ['ORGANIZATION_CONTEXT_FORBIDDEN', 'ORGANIZATION_CONTEXT_INVALID'].includes(errorCode(error))
}

export class OrganizationSessionStore {
	organizations = []
	selectedOrganizationId = null
	selectedOrganization = null
	currentContext = null
	membershipRole = null
	status = 'idle'
	errorCode = null
	pendingOrganizationId = null
	contextGeneration = 0
	bootstrapPromise = null

	constructor({ api = organizationApi, storage = browserStorage(), clearScopedState = clearTenantScopedState, registerInvalidation = false } = {}) {
		this.api = api
		this.storage = storage
		this.clearScopedState = clearScopedState
		makeAutoObservable(this, { api: false, storage: false, clearScopedState: false }, { autoBind: true })
		if (registerInvalidation) setTenantAccessInvalidatedHandler(this.handleAccessInvalidated)
	}

	get isReady() {
		return this.status === 'ready' && Boolean(this.selectedOrganizationId && this.currentContext)
	}

	get selectedOrganizationPresentation() {
		if (!this.selectedOrganization) return null
		return this.selectedOrganization.kind === 'PERSONAL'
			? { type: 'system-label', key: this.selectedOrganization.systemLabelKey }
			: { type: 'name', value: this.selectedOrganization.name }
	}

	bootstrap() {
		if (this.bootstrapPromise) return this.bootstrapPromise
		const promise = this.bootstrapCurrentIdentity()
		this.bootstrapPromise = promise
		promise.finally(() => {
			runInAction(() => {
				if (this.bootstrapPromise === promise) this.bootstrapPromise = null
			})
		})
		return promise
	}

	async bootstrapCurrentIdentity() {
		const generation = this.startTransition('bootstrap', null)
		try {
			const organizations = await this.api.list()
			if (!this.isCurrent(generation)) return null
			runInAction(() => { this.organizations = organizations })
			const preferred = this.readPreference()
			const candidate = organizations.find(item => item.id === preferred)
				|| organizations.find(item => item.kind === 'PERSONAL')
			if (!candidate) return this.finishInvalid(generation, 'ORGANIZATION_CONTEXT_UNAVAILABLE')
			return await this.validateAndAccept(candidate.id, generation)
		} catch (error) {
			return this.finishFailure(generation, error)
		}
	}

	async switchOrganization(organizationId) {
		const normalized = String(organizationId ?? '')
		if (!this.organizations.some(item => item.id === normalized)) {
			return this.invalidate('ORGANIZATION_CONTEXT_FORBIDDEN')
		}
		const generation = this.startTransition('switching', normalized)
		try {
			return await this.validateAndAccept(normalized, generation)
		} catch (error) {
			return this.finishFailure(generation, error)
		}
	}

	async refreshOrganizations() {
		try {
			const organizations = await this.api.list()
			const selectedId = this.selectedOrganizationId
			if (selectedId && !organizations.some(item => item.id === selectedId)) {
				runInAction(() => { this.organizations = organizations })
				return this.invalidate('ORGANIZATION_CONTEXT_FORBIDDEN')
			}
			runInAction(() => { this.organizations = organizations })
			if (!selectedId) return organizations
			const generation = this.startTransition('refreshing', selectedId)
			return await this.validateAndAccept(selectedId, generation)
		} catch (error) {
			const generation = this.contextGeneration
			return this.finishFailure(generation, error)
		}
	}

	async refreshOrganizationList() {
		try {
			const organizations = await this.api.list()
			const selectedId = this.selectedOrganizationId
			if (selectedId && !organizations.some(item => item.id === selectedId)) {
				runInAction(() => { this.organizations = organizations })
				return this.invalidate('ORGANIZATION_CONTEXT_FORBIDDEN')
			}
			runInAction(() => { this.organizations = organizations })
			return organizations
		} catch (error) {
			runInAction(() => { this.errorCode = errorCode(error) })
			throw error
		}
	}

	logout() {
		this.storage?.removeItem(SELECTED_ORGANIZATION_STORAGE_KEY)
		this.startTransition('idle', null)
		runInAction(() => { this.organizations = [] })
	}

	handleAccessInvalidated(details) {
		if (details?.generation !== undefined && !this.isCurrent(details.generation)) return null
		return this.invalidate(details?.code || 'ORGANIZATION_CONTEXT_FORBIDDEN')
	}

	invalidate(code) {
		const generation = this.startTransition('invalid', null)
		this.storage?.removeItem(SELECTED_ORGANIZATION_STORAGE_KEY)
		runInAction(() => {
			if (this.isCurrent(generation)) this.errorCode = code
		})
		return null
	}

	startTransition(status, pendingOrganizationId) {
		const snapshot = beginTenantContextTransition()
		this.clearScopedState(status)
		runInAction(() => {
			this.contextGeneration = snapshot.generation
			this.selectedOrganizationId = null
			this.selectedOrganization = null
			this.currentContext = null
			this.membershipRole = null
			this.pendingOrganizationId = pendingOrganizationId
			this.status = status
			this.errorCode = null
		})
		return snapshot.generation
	}

	async validateAndAccept(organizationId, generation) {
		const context = await this.api.context(organizationId, generation)
		if (!this.isCurrent(generation)) return null
		const listed = this.organizations.find(item => item.id === context.organization.id)
		if (!listed || context.membership.status !== 'ACTIVE') {
			return this.finishInvalid(generation, 'ORGANIZATION_CONTEXT_FORBIDDEN')
		}
		if (!acceptTenantContext(context.organization.id, generation)) return null
		runInAction(() => {
			this.selectedOrganizationId = context.organization.id
			this.selectedOrganization = context.organization
			this.currentContext = context
			this.membershipRole = context.membership.role
			this.pendingOrganizationId = null
			this.status = 'ready'
			this.errorCode = null
		})
		this.storage?.setItem(SELECTED_ORGANIZATION_STORAGE_KEY, context.organization.id)
		return context
	}

	finishFailure(generation, error) {
		if (!this.isCurrent(generation)) return null
		if (isAccessInvalid(error)) return this.finishInvalid(generation, errorCode(error))
		runInAction(() => {
			this.status = 'error'
			this.errorCode = errorCode(error)
			this.pendingOrganizationId = null
		})
		return null
	}

	finishInvalid(generation, code) {
		if (!this.isCurrent(generation)) return null
		this.storage?.removeItem(SELECTED_ORGANIZATION_STORAGE_KEY)
		runInAction(() => {
			this.status = 'invalid'
			this.errorCode = code
			this.pendingOrganizationId = null
			this.selectedOrganizationId = null
			this.selectedOrganization = null
			this.currentContext = null
			this.membershipRole = null
		})
		return null
	}

	isCurrent(generation) {
		return generation === this.contextGeneration
	}

	readPreference() {
		const value = this.storage?.getItem(SELECTED_ORGANIZATION_STORAGE_KEY)
		return /^[1-9]\d*$/.test(value || '') ? value : null
	}
}

const organizationSessionStore = new OrganizationSessionStore({ registerInvalidation: true })
export default organizationSessionStore
